import { forwardRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CallLogForm } from "@/pages/call-logs/index";
import { api, attachmentDownloadUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { cn, formatDateTime } from "@/lib/utils";
import {
    Activity,
    Camera,
    CheckCircle2,
    ClipboardList,
    Loader2,
    MessageSquare,
    Phone,
    Plus,
    Mail,
    Send,
    Settings2,
    User,
    X,
} from "lucide-react";
import { groupTimeline, type FeedCategory, type FeedEntry, type TimelineItemLike } from "../jobDetailHelpers";
import { SentEmailDialog } from "@/components/sent-email-dialog";
import { isOtherWaySend, mailroomThreadUrl, useSendHistory, type SendHistoryEntry } from "@/lib/use-send-history";
import { otherWayHow } from "@/components/sent-another-way-dialog";
import { Link } from "react-router-dom";
import { JobNoteActions, JobNotesPanel } from "./notes-panel";
import { EmptyRow } from "./empty-row";
import type { JobDetail } from "./types";

export type Composer = "note" | "message" | "call" | null;
export type Filter = "all" | FeedCategory;

export const FILTER_CHIPS: Array<{ key: Filter; label: string }> = [
    { key: "all", label: "All" },
    { key: "note", label: "Notes" },
    { key: "milestone", label: "Milestones" },
    { key: "comms", label: "Comms" },
    { key: "files", label: "Files" },
    { key: "system", label: "System" },
];

// All is the landing view, and the last chip you picked is remembered (audit
// 2026-10-01). History: #1382 made Notes the forced default and dropped the
// memory, because while #1377 hid notes people clicked All to find anything
// and it stuck. Notes now render as ordinary feed rows under All (#1377), so
// opening on Notes only hid the job's story — test results, status changes,
// invoices — behind a chip. Per-browser, best effort: storage that throws
// (private mode, blocked site data) just means All every time.
const DEFAULT_FILTER: Filter = "all";
const FILTER_STORAGE_KEY = "gwc.job-activity.filter";

export function readStoredFilter(): Filter {
    try {
        const v = window.localStorage.getItem(FILTER_STORAGE_KEY);
        return FILTER_CHIPS.some((c) => c.key === v) ? (v as Filter) : DEFAULT_FILTER;
    } catch {
        return DEFAULT_FILTER;
    }
}

export function storeFilter(f: Filter): void {
    try {
        window.localStorage.setItem(FILTER_STORAGE_KEY, f);
    } catch {
        // Not remembered — harmless; the chip still switches.
    }
}

// Issue #768: the feed opens with the latest few events + "Show all N" —
// the unbounded feed was the page's #1 height offender.
const FEED_CAP = 5;

const CATEGORY_ICON: Record<FeedCategory, { Icon: typeof Activity; cls: string }> = {
    note: { Icon: ClipboardList, cls: "text-amber-500" },
    comms: { Icon: Phone, cls: "text-teal-500" },
    files: { Icon: Camera, cls: "text-purple-500" },
    milestone: { Icon: CheckCircle2, cls: "text-emerald-600" },
    system: { Icon: Settings2, cls: "text-muted-foreground" },
};

/** A single job-note row carries its id + pin state in meta (activity.ts
 *  `translateJobNote`); grouped runs and every other row return null. */
export function noteMeta(
    entry: FeedEntry,
): { noteId: string; pinned: boolean; authorUserId: string | null; createdAt: string } | null {
    if (entry.items.length !== 1) return null;
    const meta = entry.items[0]?.meta;
    const noteId = meta?.noteId;
    if (typeof noteId !== "string") return null;
    return {
        noteId,
        pinned: meta?.pinned === true,
        authorUserId: typeof meta?.authorUserId === "string" ? meta.authorUserId : null,
        createdAt: typeof meta?.createdAt === "string" ? meta.createdAt : entry.timestamp,
    };
}

/** One address's outcome, in the office's words. */
export function sendStatusLabel(r: {
    status: SendHistoryEntry["recipients"][number]["status"];
    openedAt?: string | null;
}): string {
    if (r.status === "undelivered") return "didn't arrive";
    if (r.status === "failed") return "failed";
    if (r.status === "dropped") return "got nothing";
    if (r.openedAt)
        return `opened ${new Date(r.openedAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
    return r.status === "delivered" ? "delivered" : "sent";
}

/** The send-history entry this feed row is about, or null (#1545).
 *  Matched on `send_group_id` — the id the sender stamps on both the audit
 *  event and every ledger row of the same send. A send made before that id
 *  existed is filed server-side under its own audit event (`event:<auditId>`,
 *  customerSendHistory.ts `legacyPacketAnchors`) — the event names the
 *  addresses it emailed, so nothing here guesses by timestamp. */
function packetSendFor(entry: FeedEntry, sends: SendHistoryEntry[]): SendHistoryEntry | null {
    if (entry.items.length !== 1) return null;
    const item = entry.items[0];
    const meta = item?.meta;
    if (meta?.action !== "customer.packet_send" && meta?.action !== "customer.delivered_another_way") return null;
    const groupId = meta?.send_group_id;
    if (typeof groupId === "string") return sends.find((s) => s.id === groupId) ?? null;
    const auditId = typeof item?.id === "string" && item.id.startsWith("audit:") ? item.id.slice(6) : null;
    if (!auditId) return null;
    return sends.find((s) => s.id === `event:${auditId}`) ?? null;
}

/**
 * Who got this packet, what each of them got, and whether it landed (#1545).
 *
 * The office's actual question about a job's email is never "was a packet
 * sent" — it's "did *this* person get *their* report". So the row lists every
 * address individually rather than a count, marks the two kinds of miss
 * distinctly (a delivery that failed vs. someone who received nothing because
 * their documents weren't ready), and offers the message itself.
 */
function PacketSendDetail({ send, onViewEmail }: { send: SendHistoryEntry; onViewEmail: (ledgerId: string) => void }) {
    // 2026-10-07: delivered without email. No address, no message to open.
    if (isOtherWaySend(send)) {
        const docs = send.recipients.flatMap((r) => r.documents);
        return (
            <div
                className="mt-1.5 space-y-0.5 rounded-md border border-primary/40 bg-primary/5 p-2 text-[11px]"
                data-testid="packet-send-other-way"
            >
                <div className="font-semibold text-foreground/90">
                    Sent {otherWayHow(send.channel, send.channelLabel)}
                    {send.sentByName ? ` · marked by ${send.sentByName}` : ""}
                    <span className="ml-1.5 rounded-full bg-card px-1.5 py-0.5 text-[10px] font-semibold text-primary">
                        Not an email
                    </span>
                </div>
                {docs.length > 0 && <div className="text-muted-foreground">{[...new Set(docs)].join(", ")}</div>}
                {send.note && <div className="text-foreground/80">“{send.note}”</div>}
                {send.proofAttachmentId && (
                    <a
                        href={attachmentDownloadUrl(send.proofAttachmentId, { disposition: "inline" })}
                        target="_blank"
                        rel="noreferrer"
                        className="font-medium text-primary hover:underline"
                    >
                        View proof
                    </a>
                )}
            </div>
        );
    }
    const withBody = send.recipients.find((r) => r.hasBody && r.ledgerId);
    const thread = send.recipients.find((r) => r.mailroom)?.mailroom ?? null;
    return (
        <div className="mt-1.5 space-y-1 rounded-md border bg-muted/30 p-2">
            {send.internalCopy && (
                <div className="text-[11px] font-semibold text-muted-foreground" data-testid="packet-send-test-copy">
                    Test copy · staff only. It didn&apos;t count as sent to the customer.
                </div>
            )}
            {send.recipients.map((r) => (
                <div
                    key={`${r.ledgerId ?? "dropped"}:${r.email}`}
                    className="flex flex-wrap items-baseline justify-between gap-x-2 text-[11px]"
                >
                    <span className="min-w-0">
                        <span className="font-medium text-foreground/90">{r.email}</span>
                        {r.contactName && <span className="text-foreground/55"> ({r.contactName})</span>}
                        {r.documents.length > 0 && (
                            <span className="text-foreground/55"> — {r.documents.join(", ")}</span>
                        )}
                    </span>
                    <span
                        className={cn(
                            "shrink-0 font-semibold",
                            r.status === "sent" || r.status === "delivered"
                                ? "text-muted-foreground"
                                : "text-destructive",
                        )}
                        title={
                            r.status === "sent"
                                ? "Handed to our email service; no word back from their server yet"
                                : undefined
                        }
                    >
                        {sendStatusLabel(r)}
                    </span>
                    {r.status !== "sent" && r.status !== "delivered" && r.reason && (
                        <span className="w-full text-[10.5px] text-destructive/80">{r.reason}</span>
                    )}
                </div>
            ))}
            <div className="flex flex-wrap items-center gap-3 pt-1">
                {withBody?.ledgerId ? (
                    <button
                        type="button"
                        onClick={() => onViewEmail(withBody.ledgerId!)}
                        className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline"
                    >
                        <Mail className="h-3 w-3" /> View the email
                    </button>
                ) : (
                    // Said out loud rather than hidden: a missing link here means
                    // no copy was kept, not that the email didn't happen.
                    <span className="text-[10.5px] text-muted-foreground">
                        No copy of this message was kept — it predates the send archive.
                    </span>
                )}
                {thread && (
                    <Link
                        to={mailroomThreadUrl(thread)}
                        className="text-[11px] font-medium text-primary hover:underline"
                    >
                        Open in Mailroom
                    </Link>
                )}
            </div>
        </div>
    );
}

function FeedRow({
    entry,
    jobId,
    currentUserId,
    onNoteChanged,
    packetSend,
    onViewEmail,
}: {
    entry: FeedEntry;
    jobId: string;
    currentUserId: string | null;
    onNoteChanged: () => void;
    packetSend?: SendHistoryEntry | null;
    onViewEmail: (ledgerId: string) => void;
}) {
    const { Icon, cls } = CATEGORY_ICON[entry.category];
    const note = noteMeta(entry);
    return (
        <div className="relative flex gap-3 py-2.5">
            <div className="relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border bg-card">
                <Icon className={cn("h-3.5 w-3.5", cls)} />
            </div>
            <div className="flex-1 min-w-0 pt-0.5">
                <span className={cn("text-sm", entry.category === "system" ? "text-foreground/70" : "font-medium")}>
                    {entry.title}
                </span>
                {note?.pinned && (
                    <span className="ml-1.5 rounded px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 bg-amber-100 dark:bg-amber-500/20 dark:text-amber-300">
                        pinned to the top
                    </span>
                )}
                {/* A note is the content, not a caption for it — give the body
                    its own block so it reads at full length (#1377). */}
                {note && entry.detail ? (
                    <p
                        className={cn(
                            "mt-1 rounded-md border p-2 text-sm whitespace-pre-wrap",
                            note.pinned
                                ? "border-amber-300/60 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/10"
                                : "bg-muted/40",
                        )}
                    >
                        {entry.detail}
                    </p>
                ) : (
                    entry.detail && <p className="text-xs text-foreground/80 mt-0.5">{entry.detail}</p>
                )}
                {packetSend && <PacketSendDetail send={packetSend} onViewEmail={onViewEmail} />}
                {/* Attribution first (Issue #269): lead with WHO so the office
                    can see who to ask at a glance, not bury it after the time. */}
                <div className="flex items-center gap-2 mt-0.5 text-[10px] text-foreground/60">
                    {entry.actor && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-foreground/80">
                            <User className="h-3 w-3 text-foreground/50" aria-hidden="true" />
                            {entry.actor}
                        </span>
                    )}
                    <span>{formatDateTime(entry.timestamp)}</span>
                    {entry.items.length > 1 && <span>· {entry.items.length} events</span>}
                </div>
                {note && (
                    <JobNoteActions
                        jobId={jobId}
                        noteId={note.noteId}
                        body={entry.detail ?? ""}
                        pinned={note.pinned}
                        authorUserId={note.authorUserId}
                        createdAt={note.createdAt}
                        currentUserId={currentUserId}
                        onChanged={onNoteChanged}
                    />
                )}
            </div>
        </div>
    );
}

/**
 * The inline Note / Message / Log call composers, shared by the Activity card
 * and the calm job page's Conversation (#1717). The message draft lives here,
 * so keep it mounted and pass `composer={null}` to hide it.
 */
export function ActivityComposer({
    job,
    composer,
    onDone,
}: {
    job: JobDetail;
    composer: Composer;
    onDone: () => void;
}) {
    const qc = useQueryClient();
    const jobId = job.id;
    // Message composer state (moved from the old always-open card).
    const [msgChannel, setMsgChannel] = useState<"sms" | "email">("sms");
    const [msgSubject, setMsgSubject] = useState("");
    const [msgBody, setMsgBody] = useState("");

    const invalidateActivity = () => {
        qc.invalidateQueries({ queryKey: ["activity-timeline"] });
        qc.invalidateQueries({ queryKey: ["job", jobId] });
    };

    // Ad-hoc customer message (SMS / email) scoped to this job.
    const sendMessage = useMutation({
        mutationFn: (vars: { channel: "sms" | "email"; body: string; subject?: string }) =>
            api.post<{ skipped?: string | null }>(`/api/jobs/${jobId}/message-customer`, vars),
        onSuccess: (data) => {
            if (data?.skipped === "opted_out") {
                toast.warning("Not sent — this customer has opted out of SMS.");
            } else {
                toast.success("Message sent");
            }
            setMsgBody("");
            setMsgSubject("");
            onDone();
            invalidateActivity();
        },
        onError: (e: unknown) => {
            const body = (e as { body?: { error?: string; message?: string } } | null)?.body;
            toast.error(body?.message ?? `Send failed: ${body?.error ?? "unknown error"}`);
        },
    });

    return (
        <>
            {composer === "note" && <JobNotesPanel jobId={jobId} notesRich={job.notes_rich} />}
            {composer === "message" && (
                <div className="space-y-2 rounded-md border p-3">
                    <div className="flex gap-2">
                        <Button
                            size="sm"
                            variant={msgChannel === "sms" ? "default" : "outline"}
                            onClick={() => setMsgChannel("sms")}
                        >
                            SMS
                        </Button>
                        <Button
                            size="sm"
                            variant={msgChannel === "email" ? "default" : "outline"}
                            onClick={() => setMsgChannel("email")}
                        >
                            Email
                        </Button>
                    </div>
                    {msgChannel === "email" && (
                        <Input
                            value={msgSubject}
                            onChange={(e) => setMsgSubject(e.target.value)}
                            placeholder="Subject (optional)"
                            className="text-sm"
                        />
                    )}
                    <Textarea
                        value={msgBody}
                        onChange={(e) => setMsgBody(e.target.value)}
                        rows={3}
                        placeholder={
                            msgChannel === "sms"
                                ? "Text the customer (sends to their phone on file)…"
                                : "Email the customer (sends to their email on file)…"
                        }
                        className="text-sm"
                        autoFocus
                    />
                    <div className="flex items-center justify-between">
                        <p className="text-[11px] text-foreground/60">
                            Sends to the customer&rsquo;s primary {msgChannel === "sms" ? "phone" : "email"}. SMS
                            respects opt-outs.
                        </p>
                        <Button
                            size="sm"
                            disabled={sendMessage.isPending || !msgBody.trim()}
                            onClick={() =>
                                sendMessage.mutate({
                                    channel: msgChannel,
                                    body: msgBody.trim(),
                                    ...(msgChannel === "email" && msgSubject.trim()
                                        ? { subject: msgSubject.trim() }
                                        : {}),
                                })
                            }
                        >
                            {sendMessage.isPending ? (
                                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                            ) : (
                                <Send className="h-3.5 w-3.5 mr-1" />
                            )}
                            Send
                        </Button>
                    </div>
                </div>
            )}
            {composer === "call" && (
                <CallLogForm
                    defaultJobId={jobId}
                    defaultCustomerId={job.customer_id}
                    onSuccess={() => {
                        onDone();
                        qc.invalidateQueries({ queryKey: ["call-logs"] });
                        invalidateActivity();
                        toast.success("Call logged");
                    }}
                />
            )}
        </>
    );
}

/**
 * Activity card (spec §3.3): Notes + Call Log + Message customer + Timeline
 * merged. Collapsed composer buttons expand inline; the feed is
 * `groupTimeline(GET /api/activity)` newest-first with filter chips and a
 * "Show N system events" expander; the tech note stays pinned while the job
 * is open. The old always-open composers and the duplicated Call Log header
 * are gone.
 */
export const ActivityCard = forwardRef<HTMLDivElement, { job: JobDetail }>(function ActivityCard({ job }, ref) {
    const qc = useQueryClient();
    const { user, isOffice } = useAuth();
    const jobId = job.id;
    const [composer, setComposer] = useState<Composer>(null);
    const [filter, setFilter] = useState<Filter>(readStoredFilter);
    const [systemOpen, setSystemOpen] = useState(false);
    const [showAllEvents, setShowAllEvents] = useState(false);
    // #1545 — the per-send breakdown under each packet row, and the message
    // viewer it opens. The job page already reads this (same query key), so
    // sharing it here costs no extra request.
    const [viewEmailId, setViewEmailId] = useState<string | null>(null);
    const sendHistoryQ = useSendHistory(jobId, isOffice);
    const packetSends = sendHistoryQ.data?.sends ?? [];

    const changeFilter = (next: Filter) => {
        setFilter(next);
        storeFilter(next);
        setShowAllEvents(false);
    };

    const activityQ = useQuery({
        queryKey: ["activity-timeline", "job", jobId, 50],
        queryFn: () =>
            api.get<{ timeline: TimelineItemLike[] }>(
                `/api/activity?entityType=job&entityId=${encodeURIComponent(jobId)}&limit=50`,
            ),
    });

    const invalidateActivity = () => {
        qc.invalidateQueries({ queryKey: ["activity-timeline"] });
        qc.invalidateQueries({ queryKey: ["job", jobId] });
    };

    const entries = groupTimeline(activityQ.data?.timeline ?? []);
    const nonSystem = entries.filter((e) => e.category !== "system");
    const systemCount = entries.length - nonSystem.length;
    const filtered =
        filter === "all" ? (systemOpen ? entries : nonSystem) : entries.filter((e) => e.category === filter);
    // Under the Notes chip the pinned note floats to the top — that is what
    // pinning means. The All view stays strictly chronological; it is the
    // job's story, not a notice board.
    const visible =
        filter === "note"
            ? [...filtered].sort((a, b) => Number(noteMeta(b)?.pinned ?? false) - Number(noteMeta(a)?.pinned ?? false))
            : filtered;
    // Cap the feed at the latest few events (Issue #768); "Show all N" lifts
    // it. System events stay behind their own expander regardless.
    const capped = showAllEvents ? visible : visible.slice(0, FEED_CAP);

    // 2026-10-22: the old "Tech instructions" banner moved into "What the
    // tech will do" at the top of the page — the one place the tech is told
    // things. This card is the office ↔ tech chat.

    return (
        <Card ref={ref} id="activity-card">
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                    <CardTitle className="text-sm flex items-center gap-2">
                        <Activity className="h-4 w-4" /> Activity
                    </CardTitle>
                    {/* Collapsed composers — expand inline on click */}
                    <div className="flex items-center gap-1.5">
                        {(
                            [
                                { key: "note", label: "Note", Icon: Plus },
                                { key: "message", label: "Message", Icon: MessageSquare },
                                { key: "call", label: "Log call", Icon: Phone },
                            ] as const
                        ).map(({ key, label, Icon }) => (
                            <Button
                                key={key}
                                size="sm"
                                variant={composer === key ? "default" : "outline"}
                                className="h-7 gap-1 text-xs"
                                onClick={() => setComposer(composer === key ? null : key)}
                            >
                                {composer === key ? <X className="h-3 w-3" /> : <Icon className="h-3 w-3" />}
                                {label}
                            </Button>
                        ))}
                    </div>
                </div>
            </CardHeader>
            <CardContent className="space-y-3">
                {/* Always mounted, so a half-written message survives closing the composer. */}
                <ActivityComposer job={job} composer={composer} onDone={() => setComposer(null)} />

                {/* Filter chips */}
                <div className="flex items-center gap-1.5 flex-wrap">
                    {FILTER_CHIPS.map((chip) => (
                        <button
                            key={chip.key}
                            type="button"
                            onClick={() => changeFilter(chip.key)}
                            className={cn(
                                "rounded-full border px-2.5 py-0.5 text-[11px] leading-relaxed transition-colors",
                                filter === chip.key
                                    ? "border-primary bg-primary/10 font-semibold text-primary"
                                    : "text-foreground/60 hover:text-foreground",
                            )}
                        >
                            {chip.label}
                        </button>
                    ))}
                </div>

                {/* Feed */}
                {activityQ.isLoading ? (
                    <div className="flex items-center gap-2 py-4 text-xs text-foreground/70">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading activity…
                    </div>
                ) : visible.length === 0 ? (
                    <EmptyRow
                        label={
                            filter === "all"
                                ? "No activity yet"
                                : `No ${(FILTER_CHIPS.find((c) => c.key === filter)?.label ?? filter).toLowerCase()} yet`
                        }
                    />
                ) : (
                    <div className="relative">
                        <div className="absolute left-4 top-0 bottom-0 w-px bg-border" aria-hidden />
                        <div>
                            {capped.map((entry) => (
                                <FeedRow
                                    key={entry.id}
                                    entry={entry}
                                    jobId={jobId}
                                    currentUserId={user?.id ?? null}
                                    onNoteChanged={invalidateActivity}
                                    packetSend={packetSendFor(entry, packetSends)}
                                    onViewEmail={setViewEmailId}
                                />
                            ))}
                        </div>
                    </div>
                )}
                {!showAllEvents && visible.length > FEED_CAP && (
                    <Button
                        variant="ghost"
                        className="h-11 w-full"
                        onClick={() => setShowAllEvents(true)}
                        data-testid="activity-show-all"
                    >
                        Show all {visible.length}
                    </Button>
                )}
                {filter === "all" && systemCount > 0 && (
                    <button
                        type="button"
                        className="text-xs text-foreground/60 hover:text-foreground hover:underline"
                        onClick={() => setSystemOpen((o) => !o)}
                    >
                        {systemOpen
                            ? "Hide system events"
                            : `Show ${systemCount} system event${systemCount === 1 ? "" : "s"}`}
                    </button>
                )}
            </CardContent>
            {/* #1545 — the exact message one recipient received. */}
            <SentEmailDialog
                open={!!viewEmailId}
                onClose={() => setViewEmailId(null)}
                jobId={jobId}
                ledgerId={viewEmailId}
            />
        </Card>
    );
});
